import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { DownloadSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { WeeklyFinanceCard } from "../components/WeeklyFinanceCard";
import PatternReportsCard from "../components/PatternReportsCard";

const STATE_META = {
  renewal_overdue: { label: "Past due", cls: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]" },
  initial_due: { label: "Never completed", cls: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]" },
  due_soon: { label: "Due soon", cls: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" },
  current: { label: "Current", cls: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" },
};

const UPLOAD_CARDS = [
  { key: "evv", title: "EVV compliance report", hint: "CSV/Excel from HHAeXchange — payer + caregiver compliance", accept: ".csv,.xlsx,.xls", link: ["/evv", "View EVV tracking"] },
  { key: "visits", title: "Visits export", hint: "HHAeXchange visit/schedule export", accept: ".csv,.xlsx,.xls", link: ["/reconciliation", "View reconciliation"] },
  { key: "missed", title: "Missed punch forms", hint: "JotForm PDFs — one or many at once", accept: "application/pdf", multiple: true, link: ["/evv", "Review missed punches"] },
  { key: "tb_screen", title: "Initial TB screen (signed)", hint: "Signed TB screening document — filename should contain the employee's name", accept: ".pdf,.png,.jpg,.jpeg,.webp", multiple: true, link: ["/team", "Open Team"] },
  { key: "overnight_punch", title: "Overnight punch-ins", hint: "Overnight check-in report export", accept: ".csv,.xlsx,.xls,.pdf" },
  { key: "task_codes", title: "Task codes", hint: "Task code entry / compliance report export", accept: ".csv,.xlsx,.xls,.pdf" },
  { key: "other_report", title: "Other report", hint: "Any other ops report to keep on file", accept: ".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg", multiple: true },
];
const KIND_LABELS = { overnight_punch: "Overnight punch-ins", task_codes: "Task codes", other_report: "Other" };

function UploadCard({ card, onUpload, month, setMonth }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`upload-card-${card.key}`}>
      <p className="text-sm font-semibold text-stone-800">{card.title}</p>
      <p className="mt-0.5 text-xs text-stone-400">{card.hint}</p>
      <input ref={fileRef} type="file" accept={card.accept} multiple={!!card.multiple} className="hidden"
        onChange={(e) => { onUpload(card.key, e.target.files, setBusy, setResult); e.target.value = ""; }} data-testid={`upload-input-${card.key}`} />
      {card.key === "evv" && (
        <div className="mt-2">
          <label className="mb-1 block text-xs font-medium text-stone-500">Which month is this report for?</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40 border-stone-300 bg-white text-sm" data-testid="upload-month" />
        </div>
      )}
      {card.key === "tb_screen" && (
        <p className="mt-2 rounded-md bg-accent/60 px-2.5 py-1.5 text-[11px] text-stone-500">Drop a <b>ZIP</b> of signed screens or pick individual PDFs/photos. Each file should be named with the employee's name, e.g. <b>Margaret Bruno TB.pdf</b> — that's how it lands on the right profile.</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid={`upload-btn-${card.key}`}>
          <UploadSimple size={14} className="mr-1.5" /> {busy ? "Uploading…" : "Choose file & upload"}
        </Button>
        {card.link && <Link to={card.link[0]} className="text-xs font-medium text-primary hover:underline" data-testid={`upload-link-${card.key}`}>{card.link[1]} →</Link>}
      </div>
      {result && <p className="mt-2 text-xs text-stone-500" data-testid={`upload-result-${card.key}`}>{result}</p>}
    </div>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [topicFilter, setTopicFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("actionable");
  const [empSearch, setEmpSearch] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/reports/training").then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); setData({ topics: [], rows: [] }); });
  }, [isAdmin]);

  const flat = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const row of data.rows) {
      for (const e of row.entries) {
        out.push({ ...row, ...e });
      }
    }
    return out;
  }, [data]);

  const filtered = useMemo(() => {
    let f = flat;
    if (topicFilter !== "all") f = f.filter((r) => r.topic_id === topicFilter);
    if (statusFilter === "actionable") f = f.filter((r) => r.state !== "current");
    else if (statusFilter === "past_due") f = f.filter((r) => r.state === "renewal_overdue" || r.state === "initial_due");
    else if (statusFilter === "due_soon") f = f.filter((r) => r.state === "due_soon");
    else if (statusFilter === "current") f = f.filter((r) => r.state === "current");
    if (empSearch.trim()) {
      const q = empSearch.trim().toLowerCase();
      f = f.filter((r) => (r.name || "").toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
    }
    return [...f].sort((a, b) => b.days_overdue - a.days_overdue || (a.name || "").localeCompare(b.name || "") || a.title.localeCompare(b.title));
  }, [flat, topicFilter, statusFilter, empSearch]);

  const summary = useMemo(() => {
    const pastDue = flat.filter((r) => r.state === "renewal_overdue" || r.state === "initial_due");
    const dueSoon = flat.filter((r) => r.state === "due_soon");
    return {
      staffPastDue: new Set(pastDue.map((r) => r.user_id)).size,
      pastDueItems: pastDue.length,
      dueSoonItems: dueSoon.length,
      staffTotal: data ? data.rows.length : 0,
    };
  }, [flat, data]);

  const [uploadsList, setUploadsList] = useState([]);
  const [reportMonth, setReportMonth] = useState("");

  const loadReportUploads = () => api.get("/reports/uploads").then((r) => setUploadsList(r.data.uploads || [])).catch(() => {});
  useEffect(() => { if (isAdmin) loadReportUploads(); }, [isAdmin]);

  const uploadReport = async (key, files, setCardBusy, setCardResult) => {
    if (!files?.length) return;
    setCardBusy(true); setCardResult(null);
    try {
      const fd = new FormData();
      let url = "";
      if (key === "evv") { fd.append("file", files[0]); url = `/evv/upload${reportMonth ? `?month=${reportMonth}` : ""}`; }
      else if (key === "visits") { fd.append("file", files[0]); url = "/visits/import"; }
      else if (key === "missed") { for (const f of files) fd.append("files", f); url = "/missed-punch/upload"; }
      else if (key === "tb_screen") { for (const f of files) fd.append("files", f); url = "/tb-screen/upload"; }
      else { for (const f of files) fd.append("files", f); url = `/reports/upload?kind=${key}${reportMonth ? `&month=${reportMonth}` : ""}`; }
      const { data } = await api.post(url, fd, { timeout: 120000 });
      if (key === "evv") setCardResult(`Matched ${data.matched} caregiver rows${data.payer_rows ? `, ${data.payer_rows} payer rows` : ""}${data.unmatched?.length ? ` — unmatched: ${data.unmatched.join(", ")}` : ""}`);
      else if (key === "visits") setCardResult(`Imported ${data.inserted} visits (${data.duplicates} duplicates skipped)`);
      else if (key === "missed") setCardResult(`Imported ${data.imported} form(s)${data.unmatched?.length ? ` — no employee match: ${data.unmatched.join(", ")}` : ""}`);
      else if (key === "tb_screen") setCardResult(`Attached ${data.attached} signed TB screen(s)${data.replaced ? ` (${data.replaced} replaced an existing file)` : ""}${data.unmatched?.length ? ` — no staff match: ${data.unmatched.join(", ")}` : ""}`);
      else { setCardResult(`Saved ${data.saved?.length || 0} file(s)`); loadReportUploads(); }
      toast.success("Upload complete");
    } catch (e) { toast.error(formatApiError(e)); }
    setCardBusy(false);
  };

  const viewUpload = async (u) => {
    try {
      const r = await api.get(`/reports/uploads/${u.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: r.headers["content-type"] || "application/octet-stream" })), "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delUpload = async (u) => {
    if (!window.confirm(`Delete "${u.filename}"?`)) return;
    try { await api.delete(`/reports/uploads/${u.id}`); setUploadsList((l) => l.filter((x) => x.id !== u.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    const header = "Employee,Email,Hire date,Training,Status,Completed,Completed as,Due date,Days overdue";
    const lines = filtered.map((r) => [
      `"${(r.name || "").replace(/"/g, '""')}"`, r.email, r.start_date || "", `"${r.title.replace(/"/g, '""')}"`,
      STATE_META[r.state]?.label || r.state, r.completed || "", (r.completed_as || "").replace(/"/g, '""'), r.due_date || "", r.days_overdue,
    ].join(","));
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `training-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!isAdmin) {
    return <div className="py-16 text-center text-sm text-stone-500" data-testid="reports-admin-only">Reports are available to admins only.</div>;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="reports-title">Reporting Center</h1>
          <p className="mt-1 text-sm text-stone-500">Training compliance across the roster — filter by training or by employee.</p>
        </div>
        <Button onClick={exportCsv} disabled={!filtered.length} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="report-export-button">
          <DownloadSimple size={15} className="mr-1.5" /> Export CSV ({filtered.length})
        </Button>
      </div>

      <PatternReportsCard />

      <div data-testid="report-finance-section">
        <h2 className="font-heading text-lg font-semibold text-stone-900">Weekly billing & payroll</h2>
        <p className="mb-3 text-xs text-stone-400">Company-wide weekly finance snapshot — billed, paid, and payroll.</p>
        <WeeklyFinanceCard />
      </div>

      <div data-testid="report-uploads-section">
        <div>
          <h2 className="font-heading text-lg font-semibold text-stone-900">Report uploads</h2>
          <p className="text-xs text-stone-400">Drop the latest exports here — EVV, visits, missed punches, overnight punch-ins, task codes. Click a card's button, pick the file, done.</p>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {UPLOAD_CARDS.map((c) => <UploadCard key={c.key} card={c} onUpload={uploadReport} month={reportMonth} setMonth={setReportMonth} />)}
        </div>
        {uploadsList.length > 0 && (
          <div className="mt-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid="report-uploads-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Recent report files</p>
            <div className="mt-2 divide-y divide-stone-100">
              {uploadsList.slice(0, 15).map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`upload-row-${u.id}`}>
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500">{KIND_LABELS[u.kind] || u.kind}</span>
                  <button onClick={() => viewUpload(u)} className="font-medium text-stone-800 hover:text-primary hover:underline" data-testid={`upload-view-${u.id}`}>{u.filename}</button>
                  {u.month && <span className="text-xs text-stone-400">{u.month}</span>}
                  <span className="text-xs text-stone-400">{(u.uploaded_at || "").slice(0, 10)}</span>
                  <button onClick={() => delUpload(u)} className="ml-auto text-stone-300 hover:text-[#C0392B]" data-testid={`upload-delete-${u.id}`} title="Delete file"><Trash size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Staff past due", summary.staffPastDue, "report-summary-staff-past-due", "text-[#C0392B]"],
          ["Past-due items", summary.pastDueItems, "report-summary-past-due-items", "text-[#C0392B]"],
          ["Due soon", summary.dueSoonItems, "report-summary-due-soon", "text-[#8a5d14]"],
          ["Staff on roster", summary.staffTotal, "report-summary-staff-total", "text-stone-900"],
        ].map(([label, val, testid, cls]) => (
          <div key={testid} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={testid}>
            <p className={`font-heading text-2xl font-bold ${cls}`}>{val}</p>
            <p className="text-xs text-stone-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2" data-testid="report-filters">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Training</label>
          <Select value={topicFilter} onValueChange={setTopicFilter}>
            <SelectTrigger className="h-9 w-64 border-stone-300 bg-white text-sm" data-testid="report-topic-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All trainings</SelectItem>
              {(data?.topics || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Employee</label>
          <Input value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} placeholder="Search name or email…"
            className="h-9 w-56 border-stone-300 bg-white text-sm" data-testid="report-employee-search" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-48 border-stone-300 bg-white text-sm" data-testid="report-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="actionable">Needs attention</SelectItem>
              <SelectItem value="past_due">Past due only</SelectItem>
              <SelectItem value="due_soon">Due soon only</SelectItem>
              <SelectItem value="current">Current only</SelectItem>
              <SelectItem value="all">Everything</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="ml-auto text-xs text-stone-400" data-testid="report-count">{filtered.length} result{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {data === null ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="report-table">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                <th className="px-6 py-3.5 font-medium">Employee</th>
                <th className="px-6 py-3.5 font-medium">Training</th>
                <th className="px-6 py-3.5 font-medium">Status</th>
                <th className="px-6 py-3.5 font-medium">Completed</th>
                <th className="px-6 py-3.5 font-medium">Due</th>
                <th className="px-6 py-3.5 font-medium">Days overdue</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-stone-400" data-testid="report-empty">No results for these filters.</td></tr>
              )}
              {filtered.slice(0, 500).map((r, i) => (
                <tr key={`${r.user_id}-${r.topic_id}`} className="border-b border-stone-100 last:border-0" data-testid={`report-row-${i}`}>
                  <td className="px-6 py-3.5">
                    <Link to={`/team/${r.user_id}`} className="font-medium text-stone-900 hover:text-primary hover:underline" data-testid={`report-employee-link-${i}`}>{r.name || r.email}</Link>
                    {r.start_date && <span className="ml-2 text-xs text-stone-400">hired {r.start_date}</span>}
                  </td>
                  <td className="px-6 py-3.5 text-stone-700">
                    {r.title}
                    {r.completed_as && <div className="mt-0.5 text-xs text-stone-400" data-testid={`report-completed-as-${i}`}>completed as “{r.completed_as}”</div>}
                  </td>
                  <td className="px-6 py-3.5">
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATE_META[r.state]?.cls}`} data-testid={`report-status-${i}`}>
                      {STATE_META[r.state]?.label || r.state}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-stone-600">{r.completed || "—"}</td>
                  <td className="px-6 py-3.5 text-stone-600">{r.due_date || "—"}</td>
                  <td className="px-6 py-3.5 font-medium text-stone-700" data-testid={`report-days-${i}`}>{r.days_overdue > 0 ? r.days_overdue : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 500 && <p className="border-t border-stone-100 px-6 py-2 text-xs text-stone-400">Showing first 500 — export CSV for the full set.</p>}
        </div>
      )}
    </div>
  );
}
